import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Avatar,
  Tooltip,
} from "@mui/material";
import DevicesIcon from "@mui/icons-material/Devices";
import LaptopIcon from "@mui/icons-material/Laptop";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import TabletIcon from "@mui/icons-material/Tablet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PublicIcon from "@mui/icons-material/Public";

// 1. 辅助函数：简单解析 User Agent 获取设备和浏览器信息
const parseUserAgent = (ua) => {
  if (!ua) return { device: "Unknown Device", browser: "Unknown Browser" };

  let device = "Desktop";
  let icon = <LaptopIcon />;
  if (/Mobile|Android|iPhone/i.test(ua)) {
    device = /Tablet|iPad/i.test(ua) ? "Tablet" : "Mobile";
    icon = device === "Mobile" ? <PhoneIphoneIcon /> : <TabletIcon />;
  }

  let browser = "Browser";
  if (ua.includes("Chrome")) browser = "Chrome";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Safari")) browser = "Safari";
  else if (ua.includes("Edg")) browser = "Edge";

  return { device, browser, icon };
};

// 2. 辅助函数：格式化时间（显示为相对时间或具体日期）
const formatTime = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

// 3. 辅助函数：判断会话状态
const getSessionStatus = (session) => {
  if (session.revoked_at)
    return {
      label: "Revoked",
      color: "default",
      icon: <BlockIcon fontSize='small' />,
    };
  if (new Date(session.expires_at) < new Date())
    return {
      label: "Expired",
      color: "warning",
      icon: <AccessTimeIcon fontSize='small' />,
    };
  return {
    label: "Active",
    color: "success",
    icon: <CheckCircleIcon fontSize='small' />,
  };
};

const RefreshSessions = ({ refreshSessions, createdTime, expireDate }) => {
  const displayedSessions = refreshSessions.slice(0, 5);
  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 1 }}>
      <Stack spacing={2}>
        {/* 账户生命周期概览 */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: "background.default",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant='caption'
            color='text.secondary'
            fontWeight={700}
            display='block'
            gutterBottom
          >
            ACCOUNT LIFECYCLE
          </Typography>
          <Stack direction='row' spacing={3}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <AccessTimeIcon fontSize='small' color='action' />
              <Typography variant='body2'>
                Registered: <strong>{createdTime}</strong>
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={1}>
              <BlockIcon fontSize='small' color='action' />
              <Typography variant='body2'>
                Expiry: <strong>{expireDate}</strong>
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* 会话列表 */}
        <Stack spacing={1.5}>
          <Typography variant='subtitle2' fontWeight={700}>
            Active Sessions ({refreshSessions.length})
          </Typography>

          {displayedSessions.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              No active sessions found.
            </Typography>
          ) : (
            displayedSessions.map((s) => {
              const { device, browser, icon } = parseUserAgent(s.user_agent);
              const status = getSessionStatus(s);

              return (
                <Box
                  key={s.id}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor:
                      status.label === "Active" ? "success.light" : "divider",
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor:
                      status.label === "Active"
                        ? "rgba(76, 175, 80, 0.04)"
                        : "transparent",
                    transition: "all 0.2s",
                    "&:hover": { borderColor: "primary.main", boxShadow: 1 },
                  }}
                >
                  <Stack direction='row' spacing={2} alignItems='center'>
                    {/* 左侧设备图标 */}
                    <Avatar
                      sx={{ bgcolor: "action.hover", width: 40, height: 40 }}
                    >
                      {icon}
                    </Avatar>

                    {/* 中间详细信息 */}
                    <Stack spacing={0.5}>
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <Typography variant='body2' fontWeight={700}>
                          {device} · {browser}
                        </Typography>
                        {/* 状态小标签 */}
                        <Chip
                          label={status.label}
                          size='small'
                          color={status.color}
                          variant={
                            status.label === "Active" ? "filled" : "outlined"
                          }
                          sx={{ height: 18, fontSize: "0.65rem", minWidth: 50 }}
                          icon={status.icon}
                        />
                      </Stack>

                      <Stack direction='row' alignItems='center' spacing={2}>
                        <Tooltip title={s.ip_address}>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              cursor: "help",
                            }}
                          >
                            <PublicIcon sx={{ fontSize: 12 }} /> {s.ip_address}
                          </Typography>
                        </Tooltip>
                        <Typography variant='caption' color='text.secondary'>
                          Issued: {formatTime(s.issued_at)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>

                  {/* 右侧操作按钮 */}
                  {status.label === "Active" ? (
                    <Button
                      size='small'
                      color='error'
                      variant='text'
                      sx={{ fontWeight: 600 }}
                    >
                      Revoke
                    </Button>
                  ) : (
                    <Typography variant='caption' color='text.disabled'>
                      {formatTime(s.revoked_at || s.expires_at)}
                    </Typography>
                  )}
                </Box>
              );
            })
          )}

          {/* 如果超过展示数量，显示查看更多提示（可以结合你之前的 Show More 逻辑） */}
          {refreshSessions.length > 5 && (
            <Typography
              variant='caption'
              color='text.secondary'
              textAlign='center'
              sx={{ mt: 1 }}
            >
              + {refreshSessions.length - 5} more sessions
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default RefreshSessions;
